import { memo, useState, useCallback, type ComponentType } from "react";
import {
  useRenderCount,
  useMountTiming,
  perfTimestamp,
} from "../../utils/perfLogger";
import {
  type PreOrder,
  type PreOrderFlow,
  PreOrderStatus,
} from "@/app/api/types/preOrder";
import { PreOrderFlowStatus } from "@/app/api/types/preOrderFlow";
import type { UnitResponse } from "@/app/api/types/unit";
import type { ColumnDef } from "../../config/types";
import { useFlowHoverActions } from "../../contexts/FlowHoverContext";
import { useIsSourceHovered } from "../../stores/sourceHoverStore";
import type { FlowDelineationMetadata } from "../../types/delineation.types";
import { isProposalFlow } from "../../types/proposal.types";
import { useRecapCellRegistry } from "../../contexts/RecapPrimitivesContext";
import { CustomColumnCell } from "./CustomColumnCell";
import { InlineCustomFieldCell } from "./InlineCustomFieldCell";
import { CellErrorBoundary } from "./CellErrorBoundary";
import { STATUS_ROW_COLORS } from "./StatusCell";
import { useModifyBufferOptional } from "../../contexts/ModifyBufferContext";
import {
  PENDING_ADD_ROW_CLASS,
  PENDING_DELETE_ROW_CLASS,
  PENDING_EDIT_ROW_CLASS,
} from "./styles";
import { cn } from "@/lib/utils";
import { getAiSuggestion } from "./AiChangeBadge";

interface FlowRowProps {
  flow: PreOrderFlow;
  preOrder: PreOrder;
  columns: ColumnDef[];
  availableUnits: UnitResponse[];
  disabled?: boolean;
  showPartnerColumn?: boolean;
  onFlowUpdate: (flowId: string, updates: Partial<PreOrderFlow>) => void;
  onFlowDelete: (flowId: string) => void;
  getWidth?: (colId: string) => number;
}

export const FlowRow = memo(
  function FlowRow({
    flow,
    preOrder,
    columns,
    availableUnits,
    disabled,
    showPartnerColumn = false,
    onFlowUpdate,
    onFlowDelete,
    getWidth,
  }: FlowRowProps) {
    useRenderCount(`FlowRow[${flow.id.slice(0, 8)}]`);
    // Per-row mount timing — exposes the "header first, rows later" stagger.
    // When `useTransition` is active in PreOrderSection, React time-slices
    // FlowsTable's tree, so individual FlowRow useEffects fire at different
    // timestamps. Each row's `mounted in Xms` value (relative to its own
    // render start) plus the rendering order shows where the gap is.
    useMountTiming(`FlowRow[${flow.id.slice(0, 8)}]`);
    // Absolute render-start timestamp. Compare against the
    // `FlowsTable[...] render start` mark to see how far behind each row's
    // reconciliation lands. A linear progression (row 1 → row N spread over
    // 100+ms) is the smoking gun for concurrent-mode time-slicing.
    perfTimestamp(`FlowRow[${flow.id.slice(0, 8)}] render start`);
    const { setHoveredFlow, clearHover } = useFlowHoverActions();
    const isSourceHovered = useIsSourceHovered(flow.id);
    const [weightPreview, setWeightPreview] = useState<number | null>(null);
    const onWeightPreview = useCallback(
      (w: number | null) => setWeightPreview(w),
      [],
    );

    const proposalDiff = isProposalFlow(flow) ? flow.__proposalDiff : null;

    const modifyBuffer = useModifyBufferOptional();
    const isPendingAdd =
      modifyBuffer?.isModifying && modifyBuffer.isFlowPendingAdd(flow.id);
    const isPendingDelete =
      modifyBuffer?.isModifying && modifyBuffer.isFlowPendingDelete(flow.id);
    const isPendingEdit =
      modifyBuffer?.isModifying &&
      !isPendingAdd &&
      !isPendingDelete &&
      modifyBuffer.pendingInfo.editedIds.has(flow.id);

    const handleMouseEnter = useCallback(() => {
      // Whether this flow has anything to point at — an attachment
      // delineation or a mail-body quote — is `setHoveredFlow`'s call, not the
      // row's. Two gates in two files is how the body path was silently
      // dropped before it reached the highlighter (STK-1811).
      if (flow.flow_metadata) {
        const metadata = flow.flow_metadata as FlowDelineationMetadata;
        setHoveredFlow(flow.id, metadata, flow.status);
      }
    }, [flow.id, flow.flow_metadata, flow.status, setHoveredFlow]);

    let pendingRowClass = "";
    if (isPendingAdd) pendingRowClass = PENDING_ADD_ROW_CLASS;
    else if (isPendingDelete) pendingRowClass = PENDING_DELETE_ROW_CLASS;
    else if (isPendingEdit) pendingRowClass = PENDING_EDIT_ROW_CLASS;

    // Exported pre-orders override the per-flow status tint so the whole
    // row reads as locked/exported (green) regardless of flow.status.
    const isExportedPreOrder = preOrder.status === PreOrderStatus.EXPORTED;

    // AI suggestion derivations — drive the violet row chrome. The row
    // surfaces the AI signal until the server clears the metadata block
    // (after an approve/decline RPC); local UI state no longer tracks
    // per-flow resolution since accept/decline now act at PO level.
    const cellRegistry = useRecapCellRegistry();
    const suggestion = getAiSuggestion(flow);
    const isAiNew = flow.status === PreOrderFlowStatus.AI_PROPOSAL;
    const hasPendingSuggestion = suggestion?.status === "pending";
    const showAiStyling = isAiNew || hasPendingSuggestion;
    const isPendingDeleteSuggestion =
      showAiStyling && suggestion?.type === "delete";

    let baseRowClass = "";
    if (pendingRowClass) {
      baseRowClass = pendingRowClass;
    } else if (isExportedPreOrder) {
      // Exported takes priority over AI/proposal tints — the row is locked
      // and the green tint is the documented contract (see comment above).
      baseRowClass = "bg-green-50/60";
    } else if (proposalDiff) {
      baseRowClass = "bg-violet-50/50";
    } else if (showAiStyling && !isAiNew) {
      // V3 AI modify-on-existing flow: tint the whole row so it reads as
      // "AI-affected", matching the legacy proposalDiff treatment. The
      // AI-new path (isAiNew) gets a stronger tint applied below in cn().
      baseRowClass = "bg-violet-50/50";
    } else {
      baseRowClass = STATUS_ROW_COLORS[flow.status ?? 0] || "";
    }

    return (
      <tr
        data-testid="flow-row"
        data-flow-id={flow.id}
        data-source-hovered={isSourceHovered ? "true" : undefined}
        data-pending-edit={isPendingEdit ? "true" : undefined}
        data-pending-add={isPendingAdd ? "true" : undefined}
        data-pending-delete={isPendingDelete ? "true" : undefined}
        data-ai-suggestion={
          showAiStyling ? (suggestion?.type ?? (isAiNew ? "add" : null)) : null
        }
        onMouseEnter={handleMouseEnter}
        onMouseLeave={clearHover}
        className={cn(
          "group/row border-b border-gray-300 transition-colors",
          baseRowClass,
          "hover:bg-gray-50/50",
          showPartnerColumn && "cursor-pointer",
          // AI styling layers on top of the buffered/proposal/status base.
          // Whole-line AI add: dashed violet outline + thick left dent.
          // Modify/delete on existing row: just the left dent (the row
          // keeps its underlying status color so users still see context).
          showAiStyling &&
            isAiNew &&
            "border-l-[3px] border-l-violet-500 bg-violet-50/80 outline outline-1 outline-dashed outline-violet-400 -outline-offset-1 hover:bg-violet-100/70",
          showAiStyling && !isAiNew && "border-l-[3px] border-l-violet-500",
          // Delete-suggestion: horizontal strike across the whole row,
          // padded right so it clears the status/actions buttons (kept
          // legible by z-indexing those cells above the line).
          isPendingDeleteSuggestion &&
            "relative after:content-[''] after:absolute after:left-4 after:right-[200px] after:top-1/2 after:h-px after:bg-violet-400 after:pointer-events-none",
          // The row background is already spoken for (status / AI / exported /
          // modify buffer), so the reverse hover marks the row instead of
          // overwriting the colour the user is checking. `ring-*` renders as
          // box-shadow, which browsers do not paint on a <tr> under the
          // table's default border-collapse — the AI-new emphasis three
          // lines up already learned this and uses `outline` instead.
          isSourceHovered && "outline outline-2 -outline-offset-2 outline-ring",
        )}
      >
        {columns.map((col) => {
          // Flag-defined custom value columns route to a single parametrized
          // cell (it reads its `flow_metadata` key from `col`); inline custom
          // fields route to InlineCustomFieldCell; everything else resolves
          // through the active cell registry.
          const Cell: ComponentType<any> | undefined = col.customColumnKey
            ? CustomColumnCell
            : col.customFieldDef
              ? InlineCustomFieldCell
              : cellRegistry[col.id];
          if (!Cell) {
            return (
              <td
                key={col.id}
                data-col-id={col.id}
                style={{
                  width: getWidth ? `${getWidth(col.id)}px` : col.width,
                }}
              />
            );
          }
          // Status & actions cells stay above the strikethrough line so the
          // existing controls remain legible on delete suggestions.
          const aboveLine =
            isPendingDeleteSuggestion &&
            (col.id === "status" || col.id === "actions");
          return (
            <td
              key={col.id}
              data-col-id={col.id}
              style={{
                width: getWidth ? `${getWidth(col.id)}px` : col.width,
              }}
              className={cn("px-1 py-1", aboveLine && "relative z-10 bg-white")}
            >
              <CellErrorBoundary resetKey={flow.id}>
                <Cell
                  flow={flow}
                  preOrder={preOrder}
                  column={col}
                  availableUnits={availableUnits}
                  disabled={disabled}
                  onFlowUpdate={onFlowUpdate}
                  onFlowDelete={onFlowDelete}
                  weightPreview={weightPreview}
                  onWeightPreview={onWeightPreview}
                  proposalDiff={proposalDiff}
                />
              </CellErrorBoundary>
            </td>
          );
        })}
      </tr>
    );
  },
  (prev, next) =>
    prev.flow === next.flow &&
    prev.preOrder.id === next.preOrder.id &&
    prev.preOrder.partner_id === next.preOrder.partner_id &&
    prev.preOrder.location_id === next.preOrder.location_id &&
    prev.preOrder.status === next.preOrder.status &&
    prev.columns === next.columns &&
    prev.availableUnits === next.availableUnits &&
    prev.disabled === next.disabled &&
    prev.showPartnerColumn === next.showPartnerColumn &&
    prev.onFlowUpdate === next.onFlowUpdate &&
    prev.onFlowDelete === next.onFlowDelete &&
    prev.getWidth === next.getWidth,
);
